import { decode, encode } from 'qss';
import { isEmpty } from 'lodash';
import { IS_DEV, ARTICLE_PATH, PROFILE_PATH } from '../../../constants/config';
import { activeTabInterval } from './helpers';
import { normalizeText } from './stringUtils';

/**
 * Generates query params
 *
 * @example
 * encodeQuery({page: 1, perPage: 15})
 * => "?page=1&perPage=15"
 *
 * @param {object} params
 */
export function encodeQuery(params, prefix = '?') {
  const queryParams = encode(params);
  if (!queryParams) return '';
  return prefix + queryParams;
}

/**
 * Returns decoded keys and values
 *
 * @example
 * decodeQuery('foo=hello&bar=1&bar=2&bar=3&baz=true')
 * => { foo:'hello', bar:[1,2,3], baz:true };
 *
 * @param {string} query
 */
export function decodeQuery(query) {
  const queryParams = decode(query);
  if (!queryParams) return '';
  return queryParams;
}

/**
 * Get Article url
 *
 * @param {Object} options
 * @param {Object} options.article article object
 * @param {string} options.article._id article id
 * @param {string} options.article.title article title
 * @param {Object} [options.query] query params example: { utm_source: 'test' } - optional
 * @returns {String}
 *
 * @example default
 * https://onedio.com/haber/title-text-940293
 *
 */
export function getArticleUrl({ article, query } = {}) {
  if (!article || isEmpty(article)) return '';
  if (article.redirectUrl) return article.redirectUrl;
  if (article.route) return article.route;
  const queryParams = encodeQuery({ ...query });
  const id = article.legacyId;
  const articleSlug = normalizeText(article.originalTitle || article.title);
  return `/${ARTICLE_PATH}/${articleSlug}-${id}${queryParams}`;
}

/**
 * Get Tag url
 *
 * @param {Object} options
 * @param {Object} options.tag tag object
 * @param {string} options.tag._id tag id
 * @param {string} options.tag.name tag name
 * @param {Object} [options.query] query params example: { utm_source: 'test' } - optional
 * @returns {String}
 *
 * @example
 * https://onedio.com/roket-haberleri
 */
export function getTagUrl({ tag, params } = {}) {
  if (!tag) return '';
  const queryParams = encodeQuery({ ...params });
  const url = `${tag.route}${queryParams}`;
  return url;
}

/**
 * Get Profile url
 *
 * @param {Object} options
 * @param {Object} options.user user
 * @param {Object} [options.query] query params example: { utm_source: 'test' } - optional
 * @returns {String}
 *
 * @example
 * https://onedio.com/profil/mesutkoca
 *
 */
export function getProfileUrl({ user, query } = {}) {
  if (!user || !user.username || user.isActive === false) return '';
  const queryParams = encodeQuery({ ...query });
  const username = user.username;
  return `/${PROFILE_PATH}/${username}${queryParams}`;
}

/**
 * Reload page every 5 minutes
 */
export function autoRefresh() {
  if (IS_DEV) return;
  activeTabInterval(() => window.location.reload(), 1000 * 60 * 5);
}

export const referrer = process.client && document.referrer ? document.referrer : '';
